import React from "react";
import { Box, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import {
  describeCitation,
  disambiguationSuffixes,
} from "./describeCitation";
import type { Citation, CitationGroup } from "./types";

export type CanCite = (citation: Citation) => boolean;

/**
 * Transcript citations lead with their quoted dialogue; video citations lead
 * with the source name and time window; their citedText is a generated
 * frame-range placeholder that reads as noise, and composite/frame ids and
 * evidence UUIDs are pipeline internals that never help a reviewer.
 */
function citationTooltip(group: CitationGroup): React.ReactNode {
  const suffixes = disambiguationSuffixes(group.citations);
  return (
    <Box>
      {group.citations.map((citation, index) => {
        const key = `${citation.evidenceId}-${citation.locator.raw}-${index}`;
        const spacing = index < group.citations.length - 1 ? 0.75 : 0;
        const description = describeCitation(citation);
        const source = `${description.sourceName}${suffixes.get(citation) ?? ""}`;
        return (
          <Typography
            key={key}
            variant="caption"
            component="div"
            sx={{ mb: spacing }}
          >
            {description.quote ? `\u201c${description.quote}\u201d` : source}
            <Box component="span" sx={{ display: "block", opacity: 0.75 }}>
              {description.quote
                ? `${source} \u00b7 ${description.timeRange}`
                : description.timeRange}
            </Box>
          </Typography>
        );
      })}
    </Box>
  );
}

function accessibleName(citation: Citation | undefined): string {
  if (!citation) return "citation";
  const description = describeCitation(citation);
  return (
    description.quote ?? `${description.sourceName} ${description.timeRange}`
  );
}

const citationSx = {
  color: "primary.main",
  textDecoration: "underline",
  textDecorationStyle: "dotted",
  textUnderlineOffset: "2px",
  borderRadius: 0.5,
  "&:hover, &:focus-visible": { textDecorationStyle: "solid" },
} as const;

interface CitationAnchorProps {
  children: React.ReactNode;
  group: CitationGroup;
  onCite?: (group: CitationGroup) => void;
  canCite?: CanCite;
}

/**
 * A single playable source navigates directly. Multiple playable sources use
 * a real Menu so keyboard focus, Escape, and source choice have established
 * widget semantics; the Tooltip remains informational only.
 */
export const CitationAnchor: React.FC<CitationAnchorProps> = ({
  children,
  group,
  onCite,
  canCite,
}) => {
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const menuId = React.useId();
  const playable = onCite
    ? group.citations.filter((citation) => canCite?.(citation) ?? true)
    : [];
  const interactive = Boolean(onCite && playable.length > 0);
  const primary = playable[0];

  const activate = (element: HTMLElement) => {
    if (!onCite || playable.length === 0) return;
    if (playable.length === 1) {
      onCite({ ...group, citations: playable });
      return;
    }
    setMenuAnchor(element);
  };

  const child = interactive ? (
    <Box
      component="span"
      role="button"
      tabIndex={0}
      aria-label={
        playable.length > 1
          ? `Choose source: ${accessibleName(primary)}`
          : `View source: ${accessibleName(primary)}`
      }
      aria-haspopup={playable.length > 1 ? "menu" : undefined}
      aria-controls={menuAnchor ? menuId : undefined}
      aria-expanded={playable.length > 1 ? Boolean(menuAnchor) : undefined}
      data-citation-anchor="true"
      data-citation-interactive="true"
      onClick={(event) => activate(event.currentTarget)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          activate(event.currentTarget);
        }
      }}
      sx={{ ...citationSx, cursor: "pointer" }}
    >
      {children}
    </Box>
  ) : (
    <Box
      component="span"
      tabIndex={0}
      data-citation-anchor="true"
      data-citation-interactive="false"
      sx={{ ...citationSx, cursor: "help" }}
    >
      {children}
    </Box>
  );

  return (
    <>
      <Tooltip title={citationTooltip(group)} arrow>
        {child}
      </Tooltip>
      {onCite && playable.length > 1 && (
        <Menu
          id={menuId}
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          MenuListProps={{ "aria-label": "Choose citation source" }}
        >
          {playable.map((citation, index) => {
            const description = describeCitation(citation);
            const source = `${description.sourceName}${
              disambiguationSuffixes(playable).get(citation) ?? ""
            }`;
            return (
              <MenuItem
                key={`${citation.evidenceId}-${citation.locator.raw}-${index}`}
                onClick={() => {
                  setMenuAnchor(null);
                  onCite({ ...group, citations: [citation] });
                }}
              >
                {`${source} \u00b7 ${description.timeRange}`}
                {description.quote ? ` — ${description.quote}` : ""}
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </>
  );
};
